/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  TrackingPageContext,
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
} from '../../../types';
import {
  DetailedPageInfo,
  TrackingClientState,
  TrackingClientStateHolder,
} from '../helpers/clientState';
import {exists} from '../helpers/exists';
import {SourceHistoryEventData} from '../helpers/sourceHistory';
import {stringifyEventData} from '../helpers/stringifyEventData';
import {ContentViewEventGA4} from '../trackingHandlers/trackContentView';

export interface ContentView {
  shopVersion: string;
  shopId: string;
  timestamp: number;
  contentName: string;
  search: string;
  pageType: TrackingPageTypeWithId | TrackingPageTypeWithoutId;
  pageTypeId: number | null;
  breadcrumbs: string[];
  loadingTimeInSeconds: number;
  source: SourceHistoryEventData['source'];
  // origin click is string | null as if its null we dont push this field to data layer
  originClick: string | null;
}

export interface ContentViewGA4 {
  content_name: string;
  // document.title
  title: string;
  page_type: TrackingPageTypeWithId | TrackingPageTypeWithoutId;
  page_type_id: number | null;
  // origin click is string | null as if its null we dont push this field to data layer
  click_origin: string | null;
}

const MAX_PAGE_HISTORY_LENGTH = 4;

export const contentView =
  ({
    page,
    breadcrumbsPath,
  }: {
    page: TrackingPageContext;
    breadcrumbsPath?: string | null;
  }) =>
  (
    clientState: TrackingClientStateHolder
  ): {
    eventGA4: ContentViewEventGA4;
  } => {
    let currentState = clientState.getCurrent();

    const stopLoading = Date.now();

    const currentPage: DetailedPageInfo = {
      type: 'detailed',
      page,
      timestamp: stopLoading,
    };

    const historySize = currentState.pageHistory.length;

    const nextPageHistory =
      historySize >= MAX_PAGE_HISTORY_LENGTH
        ? [
            ...currentState.pageHistory.slice(
              historySize - (MAX_PAGE_HISTORY_LENGTH - 1),
              historySize
            ),
            currentPage,
          ]
        : [...currentState.pageHistory, currentPage];

    currentState = clientState.update({
      currentPage,
      pageHistory: nextPageHistory,
    });

    if (page.type === TrackingPageTypeWithId.Category) {
      currentState = clientState.update({
        lastVisitedCategory: {id: page.id, url: page.path},
      });
    }

    if (
      page.type === TrackingPageTypeWithId.ADP &&
      nextPageHistory.length > 1
    ) {
      const previousProductPage = nextPageHistory[nextPageHistory.length - 2];

      if (previousProductPage) {
        currentState = clientState.update({
          previousProductId: previousProductPage.page.id!,
        });
      }
    }

    const breadcrumbs: string[] = [];
    breadcrumbs.push(page.type);

    if (exists(breadcrumbsPath)) {
      breadcrumbsPath.split('/').forEach(part => {
        if (part !== '' && part !== 'c') {
          breadcrumbs.push(part);
        }
      });
    }

    const currentClientState = clientState.getCurrent();
    const originClick = getOriginClick({
      currentClientState,
    });
    if (!currentClientState.landingPageTracked) {
      clientState.update({landingPageTracked: true});
    }
    const shopGender = clientState.getCurrent().shopGender;
    const page_type_id = page.id || '';

    return {
      eventGA4: {
        event: 'content_view',
        content_name: page.path,
        title: page.title!,
        page_type: page.type,
        page_type_id,
        click_origin: originClick,
        content_client_group: shopGender!,
      },
    };
  };

const getOriginClick = ({
  currentClientState,
}: {
  currentClientState: TrackingClientState;
}) => {
  const pageHistory = currentClientState.pageHistory;
  const pageHistoryLength = pageHistory.length;
  const lastVisitedPage =
    pageHistoryLength === 1
      ? pageHistory[pageHistoryLength - 1]?.page
      : pageHistory[pageHistoryLength - 2]?.page;

  const origin = currentClientState.originClick;
  const isOriginClickEnabled =
    currentClientState.featureFlags.shouldTrackOriginClick;
  const isLandingPageTracked = currentClientState.landingPageTracked;

  const originClickFromHistory = [
    lastVisitedPage?.type,
    lastVisitedPage?.id,
    origin,
  ];
  // as part of ticket ASFE-8233, we don't want to have the page ID and TYPE on direct entry
  const originClickOnLandingPage = ['', '', origin];
  const originClick = isLandingPageTracked
    ? originClickFromHistory
    : originClickOnLandingPage;

  return isOriginClickEnabled ? stringifyEventData(originClick) : null;
};
